// DeliverItemModal — capture what actually happened after a prioritized item was worked on.
// Pattern A modal (inline, no Headless UI).

import { useState } from 'react';
import { X, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import type { DeliveryType, PredictedScores } from '@/types/delivery';
import { DELIVERY_TYPE_LABELS, DELIVERY_TYPE_DESCRIPTIONS } from '@/types/delivery';
import { createItemDelivery } from '@/lib/itemDeliveryService';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useValueProposalStore } from '@/stores/prioritization/valueProposalStore';
import { resolveScale } from '@/lib/valueEstimatorRunner';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';

interface Props {
  itemId: string;
  itemTitle: string;
  predictedScores?: PredictedScores | null;
  mode?: 'initial' | 'revision';
  previousOutcome?: DeliveryType | null;
  onClose: () => void;
  onDelivered: () => void;
}

const DELIVERY_TYPES: DeliveryType[] = ['shipped', 'partial', 'cancelled'];

const TYPE_COLORS: Record<DeliveryType, string> = {
  shipped: 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  partial: 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]',
  cancelled: 'border-[var(--coral)] bg-[var(--coral-dim)] text-[var(--coral)]',
};

const TYPE_COLORS_DEFAULT = 'border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--rule)] hover:bg-[var(--surface)]';

export function DeliverItemModal({
  itemId,
  itemTitle,
  predictedScores,
  mode = 'initial',
  previousOutcome = null,
  onClose,
  onDelivered,
}: Props) {
  const isRevision = mode === 'revision';
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(previousOutcome ?? 'shipped');
  const [actualScore, setActualScore] = useState('');
  // Loop two (PRD-value-suggestion-agent §6.2): what it actually took, on each
  // attribute's own scale. The delivery trigger turns these into outcome rows.
  const [actualByAttribute, setActualByAttribute] = useState<Record<string, string>>({});
  const attributes = useAttributesStore((s) => s.attributes);
  const getAcceptedValue = useValueProposalStore((s) => s.getAcceptedValue);
  const valueAttributes = attributes
    .map((attr) => {
      const scale = resolveScale(attr);
      const accepted = scale ? getAcceptedValue(itemId, attr.key, undefined, scale.scaleId) : undefined;
      return scale && accepted ? { attr, scale, accepted: String(accepted.value) } : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
  const [notes, setNotes] = useState('');
  const [lessonsLearned, setLessonsLearned] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentUserId = useUserStore((s) => s.userId);
  const currentWorkspaceId = useTenantStore((s) =>
    s.currentWorkspaceId ?? s.getCurrentWorkspace()?.id ?? null
  );

  const hasPredicted = predictedScores?.numericScore != null;
  const predictedScore = predictedScores?.numericScore;
  const toolLabel = predictedScores?.toolName
    ? predictedScores.toolName.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!currentWorkspaceId || !currentUserId) {
      setError('Workspace or user context missing.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const parsedActual = actualScore !== '' ? Number(actualScore) : null;
      await createItemDelivery({
        itemId,
        workspaceId: currentWorkspaceId,
        deliveryType,
        predictedScores: predictedScores ?? null,
        actualMetrics:
          parsedActual != null || Object.keys(actualByAttribute).length
            ? { ...(parsedActual != null ? { score: parsedActual } : {}), ...actualByAttribute }
            : null,
        notes: notes.trim() || undefined,
        lessonsLearned: lessonsLearned.trim() || undefined,
        createdBy: currentUserId,
      });
      onDelivered();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save delivery record.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-md bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-[var(--green-dim)] flex items-center justify-center">
              <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" />
            </div>
            <span className="text-base font-semibold text-[var(--text-pri)]">
              {isRevision ? 'Record delivery revision' : 'Mark as delivered'}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Item name */}
          <p className="text-sm text-[var(--text-ter)] truncate">
            <span className="font-medium text-[var(--text-pri)]">{itemTitle}</span>
          </p>

          {isRevision && (
            <div className="flex gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <AlertTriangle className="w-4 h-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[var(--mustard)] space-y-1">
                <p className="font-semibold">This adds a new delivery record</p>
                <p>
                  {previousOutcome
                    ? `The current outcome (${DELIVERY_TYPE_LABELS[previousOutcome]}) stays in history. `
                    : 'Previous records stay in history. '}
                  Calibration and reports use the newest outcome for this item.
                </p>
              </div>
            </div>
          )}

          {/* Delivery type */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
              What happened? <span className="text-[var(--coral)]">*</span>
            </label>
            <div className="grid grid-cols-1 gap-2">
              {DELIVERY_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setDeliveryType(type)}
                  className={`relative p-3  border text-left transition-all ${
                    deliveryType === type ? TYPE_COLORS[type] : TYPE_COLORS_DEFAULT
                  }`}
                >
                  <span className="text-sm font-medium block">{DELIVERY_TYPE_LABELS[type]}</span>
                  <span className="text-xs opacity-70 mt-0.5 block">{DELIVERY_TYPE_DESCRIPTIONS[type]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Predicted vs actual */}
          {hasPredicted && (
            <div className="space-y-3 pt-1 border-t border-[var(--rule)]">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-[var(--text-sec)]">
                  Score comparison {toolLabel ? `— ${toolLabel}` : ''}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
                  <p className="text-xs text-[var(--text-ter)] mb-0.5">Predicted score</p>
                  <p className="text-lg font-semibold text-[var(--text-sec)]">
                    {predictedScore != null ? predictedScore.toFixed(1) : '—'}
                  </p>
                </div>
                <div className="p-3 bg-[var(--bg-raised)] border border-[var(--rule)]">
                  <label className="text-xs text-[var(--text-ter)] block mb-0.5" htmlFor="actual-score">
                    Actual score <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                  </label>
                  <input
                    id="actual-score"
                    type="number"
                    step="any"
                    value={actualScore}
                    onChange={(e) => setActualScore(e.target.value)}
                    placeholder="e.g. 3.2"
                    className="w-full text-lg font-semibold bg-transparent outline-none text-[var(--text-pri)] placeholder:text-[var(--text-ter)]"
                  />
                </div>
              </div>
              <p className="text-xs text-[var(--text-ter)]">
                Enter the actual RICE / WSJF score calculated in hindsight to track prediction accuracy over time.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            {valueAttributes.length > 0 && deliveryType !== 'cancelled' && (
              <div className="space-y-3 pt-1 border-t border-[var(--rule)]">
                <p className="text-xs font-semibold text-[var(--text-sec)]">What it actually took <span className="font-normal text-[var(--text-ter)]">(optional — it calibrates the next estimate)</span></p>
                {valueAttributes.map(({ attr, scale, accepted }) => (
                  <div key={attr.key}>
                    <p className="text-xs text-[var(--text-ter)] mb-1">{attr.label} <span className="text-[var(--text-ter)]">· accepted {accepted}</span></p>
                    <div className="flex flex-wrap gap-1.5">
                      {scale.points.map((pt) => {
                        const on = actualByAttribute[attr.key] === pt.label;
                        return (
                          <label key={pt.label} className={`cursor-pointer px-2.5 py-1 text-xs border rounded-[100px] transition-colors ${on ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--slate)]'}`}>
                            <input
                              type="radio"
                              name={`actual-${attr.key}`}
                              className="sr-only"
                              checked={on}
                              onChange={() => setActualByAttribute((prev) => ({ ...prev, [attr.key]: pt.label }))}
                            />
                            {pt.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5" htmlFor="delivery-notes">
              Notes <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <textarea
              id="delivery-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What actually shipped? Any scope changes or surprises?"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] resize-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)]"
            />
          </div>

          {/* Lessons learned */}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5" htmlFor="lessons-learned">
              Lessons learned <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <textarea
              id="lessons-learned"
              rows={2}
              value={lessonsLearned}
              onChange={(e) => setLessonsLearned(e.target.value)}
              placeholder="What would you score differently next time? What assumptions were wrong?"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] resize-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none transition-all bg-[var(--bg-raised)]"
            />
          </div>

          {error && (
            <div className="flex gap-3 p-3 bg-[var(--coral-dim)] border border-[var(--coral)]">
              <AlertTriangle className="w-4 h-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[var(--coral)]">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-indigo-200 transition-all"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isRevision ? 'Save revision' : 'Save delivery record'}
          </button>
        </div>
      </form>
    </div>
  );
}
